import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Copy, Info, Loader2, MoreVertical, Pause, Play, RefreshCw, RotateCcw, Trash2, TriangleAlert } from 'lucide-react';

import {
  factoryService,
  factoryStatusLabel,
  isFactoryBatchOwnerAccepted,
  type FactoryBatch,
  type FactoryBatchPhase,
  type FactoryJob,
  type FactoryPhaseMetric,
  type FactoryWorkerQueueStatus,
} from '../../services/factoryService';
import type { FactoryDeskOutletContext } from '../deskOutlet';
import { useFactoryOrderScope } from '../orderScope';

const phaseLabel: Record<string, string> = {
  semantic_split: '语义切分',
  layout: '版面拆分',
  structure: '结构聚合',
  extract: '颗粒提取',
  figure_extract: '插图提取',
  ttq: '品题推荐',
  ipttq: '品题浓缩',
};

/** 工序卡上的单单位均耗 / Token 文案 */
const phaseMetricCaptions: Record<string, { latency: string; tokens: string }> = {
  layout: { latency: '单页均耗', tokens: '单页均 Token' },
  extract: { latency: '单页均耗', tokens: '单页均 Token' },
  figure_extract: { latency: '单图均耗', tokens: '单图均 Token' },
  ttq: { latency: '单品题均耗', tokens: '单品题均 Token' },
  ipttq: { latency: '单组均耗', tokens: '单组均 Token' },
};

const phaseHint: Record<string, string> = {
  layout: '关跨页=页图准备；开跨页=文档块切分；完成后判定学科',
  semantic_split: 'layout 之后 · 默认单页；开启跨页后做文档块接缝判定',
  structure: '单页直通时跳过；跨页模式=文档块 + 接缝合并',
  extract: '单页直通含抽题 + RFE/PAP；背压=页面覆盖（成功页/应提取页）',
  figure_extract: '颗粒提取之后 · 带插图颗粒再制作试验台（多家 edit 生图对照，框架）',
  ttq: '入围聚类 → 写入成品智能体；背压=知识颗粒覆盖（绑定/提取颗粒）',
  ipttq: '按成品智能体写 skill / 生成诱导题库；背压=诱导实际/目标（max(语料段数, 成员÷memberRatio)，求和）',
};

const phaseStatusLabel: Record<string, string> = {
  pending: '等待前序',
  planned: '计划中',
  blocked_by_auth: '待 Owner 授权',
  queued: '排队中',
  running: '运行中',
  succeeded: '已完成',
  failed: '失败',
  not_wired: '待接入',
  awaiting_discipline: '待学科判定',
  skipped_unsupported_discipline: '非数学·暂不提取',
  skipped_optional: '单页',
  needs_manual: '异常·需手动再试',
  backpressure: '背压补齐中',
};

const disciplineLabel: Record<string, string> = {
  math: '数学',
  yuwen: '语文',
  chinese: '语文',
  english: '英语',
  physics: '物理',
  chemistry: '化学',
  biology: '生物',
  history: '历史',
  geography: '地理',
  politics: '政治',
  other: '其他',
  unknown: '未判定',
};

const PHASE_JOB_TYPE: Record<string, string> = {
  semantic_split: 'semantic_split',
  layout: 'layout',
  structure: 'aggregate',
  extract: 'extract',
  ttq: 'ttq',
  ipttq: 'ipttq',
};

const PROGRESS_LABEL_ZH: Record<string, string> = {
  queued: '已排队，等待厂工空闲领取',
  running: '运行中…',
  loading_assets: '加载素材…',
  semantic_split_start: '跨页接缝分析…',
  semantic_split_done: '跨页语义切分完成',
  semantic_split_skipped_optional: '单页语义切分（无跨页）',
  layout_page_count_done: '页计数完成',
  layout_mineru_done: '文档块切分完成',
  layout_blocks_done: '文档块切分与学科判定完成',
  page_direct_prep_done: '页图准备与学科判定完成',
  page_direct_prep: '页图准备…',
  judge_discipline_start: '判定学科…',
  extract_page: '逐页提取…',
  aggregate_done: '结构聚合完成',
  aggregating: '结构聚合中…',
  extracting: '颗粒提取中…',
  extract_done: '颗粒提取完成',
  extract_pages_backpressure: '页面覆盖未达标 · 自动补抽',
  extract_pages_exhausted: '页面覆盖未达标 · 需手动再试',
  extract_skipped_discipline: '非数学·已跳过提取',
  enrich_start: '颗粒提取收尾（RFE/PAP）…',
  enrich_done: '颗粒提取完成',
  ttq_admission_start: '品题入围…',
  ttq_admission_cluster: '入围聚类分组…',
  ttq_admission_done: '品题入围完成',
  ttq_coverage_backpressure: '知识颗粒覆盖未达标 · 自动补跑',
  ttq_coverage_exhausted: '知识颗粒覆盖未达标 · 需手动再试',
  ttq_title_condense: '浓缩组标题…',
  ttq_similarity_readmit: 'LLM 相似再入围…',
  ttq_product_resolve: '写入成品智能体…',
  ttq_product_bank_stock: '写入成品智能体…',
  ipttq_start: '品题浓缩…',
  ipttq_condense: '品题浓缩中…',
  ipttq_product_stock: '品题浓缩 · 生成诱导题库…',
  ipttq_done: '品题浓缩完成',
  ipttq_bank_backpressure: '诱导题库未达标 · 自动补库',
  ipttq_bank_shortfall_exhausted: '诱导题库未达标 · 需手动再试',
  ipttq_skipped_ttq_skipped: '品题入围已跳过 · 浓缩跳过',
  ipttq_skipped_unsupported_discipline: '非支持学科 · 已跳过浓缩',
  ttq_skipped_unsupported_discipline: '非支持学科 · 已跳过入围',
  splitting_pdf: '切分 PDF…',
  failed: '失败',
  cancelled: '已取消',
  not_implemented: '工序尚未接入',
  not_wired: '待接入',
};

function formatAvgLatencyMs(ms: number | null | undefined, started: boolean): string {
  if (!started) return '未开始';
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '暂无';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const min = ms / 60_000;
  if (min < 60) return `${min.toFixed(1)}min`;
  return `${(min / 60).toFixed(1)}h`;
}

function formatAvgTokens(n: number | null | undefined, started: boolean): string {
  if (!started) return '未开始';
  if (n == null || !Number.isFinite(n) || n < 0) return '暂无';
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return `${Math.round(n)}`;
}

/** Desk poll clock — always absolute time; null → 未刷新. */
function formatClockTime(ts: number | null | undefined): string {
  if (ts == null || !Number.isFinite(ts)) return '';
  const d = new Date(ts);
  if (!Number.isFinite(d.getTime())) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

function formatDataRefreshLabel(refreshedAt: number | null): string {
  if (refreshedAt == null || !Number.isFinite(refreshedAt)) return '未刷新';
  const clock = formatClockTime(refreshedAt);
  return clock ? `更新于 ${clock}` : '未刷新';
}

/** Job.updatedAt from server — shows whether the worker is still advancing. */
function formatJobUpdatedLabel(jobUpdatedAt: string | null | undefined, nowMs: number): string {
  const t = parseTimestamp(jobUpdatedAt);
  if (t == null) return '';
  const clock = formatClockTime(t);
  const sec = Math.max(0, Math.floor((nowMs - t) / 1000));
  if (sec >= 120) {
    const min = Math.floor(sec / 60);
    return `工序 ${clock}（已 ${min} 分钟无推进）`;
  }
  return clock ? `工序 ${clock}` : '';
}

/** Worker reclaim / restart markers — not FC feedback. */
function isWorkerRequeueMarker(msg: string | null | undefined): boolean {
  const s = String(msg || '').trim();
  return (
    s === 'requeued_stale_running'
    || s === 'requeued_after_worker_restart'
    || s.startsWith('requeued_')
  );
}

/** Soft FC / page error shown while job still running. */
function liveFcFeedbackMessage(job: FactoryJob | null | undefined): string {
  if (!job?.errorMessage) return '';
  if (isWorkerRequeueMarker(job.errorMessage)) return '';
  if (String(job.progressLabel || '').startsWith('extract_fc_error:')) return '';
  const raw = String(job.errorMessage).trim();
  // Prefer short human form (strip HTML error pages).
  if (/PayloadTooLargeError|request entity too large/i.test(raw)) {
    return 'RFE/PAP 请求体过大（PayloadTooLargeError）· 失败页可在结果里「重新提取」';
  }
  if (/<!DOCTYPE html>|<html[\s>]/i.test(raw)) {
    const pre = /<pre[^>]*>([\s\S]*?)<\/pre>/i.exec(raw);
    const inner = (pre?.[1] || '')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/&nbsp;/gi, ' ')
      .replace(/<[^>]+>/g, '')
      .trim();
    const first = (inner.split(/\n/)[0] || '').trim();
    return (first || 'FC 返回 HTML 错误页').slice(0, 240);
  }
  return raw.slice(0, 240);
}

function metricForPhase(
  metrics: FactoryPhaseMetric[],
  phase: string,
): FactoryPhaseMetric | null {
  const key = phase === 'layout' ? 'layout'
    : phase === 'extract' ? 'extract'
      : phase === 'ttq' ? 'ttq'
        : phase === 'ipttq' ? 'ipttq'
          : null;
  if (!key) return null;
  return metrics.find((m) => m.key === key) || null;
}

type PhasePressure = {
  actual: number;
  target: number;
  label: string;
  needsManual: boolean;
  short: boolean;
};

function asFiniteNumber(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

function pressureFromBatchMeta(
  batch: FactoryBatch | null | undefined,
  phase: string,
): PhasePressure | null {
  const meta = (batch?.meta && typeof batch.meta === 'object') ? batch.meta : {};
  const bpRaw = meta.pipelineBackpressure;
  const bp = bpRaw && typeof bpRaw === 'object' && !Array.isArray(bpRaw)
    ? (bpRaw as Record<string, { needsManual?: boolean }>)
    : {};
  if (phase === 'extract') {
    const ignored = Math.max(0, Math.floor(asFiniteNumber(meta.extractIgnored)));
    const unit = Math.max(0, Math.floor(asFiniteNumber(meta.extractUnitCount)));
    const actual = Math.max(0, Math.floor(asFiniteNumber(meta.extractSucceeded)));
    const target = Math.max(0, unit - ignored);
    if (target <= 0 && actual <= 0) return null;
    return {
      actual,
      target,
      label: '页',
      needsManual: bp.extract_pages?.needsManual === true && target > 0 && actual < target,
      short: target > 0 && actual < target,
    };
  }
  if (phase === 'ttq') {
    const bound = Math.max(0, Math.floor(asFiniteNumber(meta.ttqCoverageBoundCount)));
    const admitted = Math.max(0, Math.floor(asFiniteNumber(meta.ttqParticleCount)));
    const recycle = Math.max(0, Math.floor(asFiniteNumber(meta.ttqRecycleCount)));
    const actual = bound > 0 ? bound : admitted + recycle;
    const target = Math.max(
      0,
      Math.floor(asFiniteNumber(meta.ttqExtractParticleCount)),
      Math.floor(asFiniteNumber(meta.extractQaTotal)),
    );
    if (target <= 0 && actual <= 0) return null;
    return {
      actual,
      target,
      label: '覆盖',
      needsManual: bp.ttq_coverage?.needsManual === true && target > 0 && actual < target,
      short: target > 0 && actual < target,
    };
  }
  if (phase === 'ipttq') {
    const actual = Math.max(0, Math.floor(asFiniteNumber(meta.ipttqBankDraftCount)));
    let target = Math.max(0, Math.floor(asFiniteNumber(meta.ipttqBankTargetSum)));
    if (target <= 0) {
      const warehouseN = Math.max(0, Math.floor(asFiniteNumber(meta.ttqWarehouseCount)));
      if (
        warehouseN > 0
        && (meta.ipttq == null || actual <= 0 || meta.ipttqBankShortfall === true)
      ) {
        target = Math.max(warehouseN, actual + 1);
      }
    }
    if (target <= 0 && actual <= 0) return null;
    return {
      actual,
      target,
      label: '诱导题',
      needsManual: bp.ipttq_bank?.needsManual === true && target > 0 && actual < target,
      short: target > 0 && actual < target,
    };
  }
  return null;
}

function resolvePhasePressure(
  batch: FactoryBatch | null | undefined,
  metric: FactoryPhaseMetric | null | undefined,
  phase: string,
): PhasePressure | null {
  const fromMeta = pressureFromBatchMeta(batch, phase);
  const metricTarget = metric?.pressureTarget == null ? 0 : Math.max(0, Math.floor(Number(metric.pressureTarget) || 0));
  const metricActual = metric?.pressureActual == null ? 0 : Math.max(0, Math.floor(Number(metric.pressureActual) || 0));
  const fromMetric = (metricTarget > 0 || (metric && metric.pressureLabel))
    ? {
      actual: metric?.pressureActual != null ? metricActual : (fromMeta?.actual || 0),
      target: metricTarget > 0 ? metricTarget : (fromMeta?.target || 0),
      label: String(metric?.pressureLabel || fromMeta?.label || '').trim(),
      needsManual: metric?.pressureNeedsManual === true,
      short: false as boolean,
    }
    : null;
  if (fromMetric) {
    fromMetric.short = fromMetric.target > 0 && fromMetric.actual < fromMetric.target;
    fromMetric.needsManual = (
      fromMetric.needsManual
      || fromMeta?.needsManual === true
    ) && fromMetric.short;
  }

  // Prefer live batch.meta when it has a real meter — usage metrics may be stale (poll skips usage).
  if (fromMeta && fromMeta.target > 0) {
    return {
      ...fromMeta,
      needsManual: (fromMeta.needsManual || fromMetric?.needsManual === true) && fromMeta.short,
    };
  }
  if (fromMetric && fromMetric.target > 0) {
    return fromMetric;
  }
  return fromMeta;
}

function batchPressureNeedsManual(
  batch: FactoryBatch | null | undefined,
  metrics?: FactoryPhaseMetric[],
): boolean {
  return ['extract', 'ttq', 'ipttq'].some(
    (phase) => resolvePhasePressure(batch, metricForPhase(metrics || [], phase), phase)?.needsManual === true,
  );
}

function batchPressureShort(
  batch: FactoryBatch | null | undefined,
  metrics?: FactoryPhaseMetric[],
): boolean {
  if (batch?.meta?.pipelineReadyBlocked === true) return true;
  return ['extract', 'ttq', 'ipttq'].some(
    (phase) => resolvePhasePressure(batch, metricForPhase(metrics || [], phase), phase)?.short === true,
  );
}

/** e.g. 页 120/126 · 颗粒 513 · 覆盖 474/513 · 诱导题 62/88 */
function formatPhaseUnitCount(
  metric: FactoryPhaseMetric | null | undefined,
  pressure?: PhasePressure | null,
): string {
  if (!metric || !metric.started) return '';
  const total = Math.max(0, Number(metric.unitCount) || 0);
  const doneRaw = metric.unitDone;
  const done = doneRaw == null ? null : Math.max(0, Number(doneRaw) || 0);
  const unit = String(metric.unitLabel || '项').trim() || '项';
  let primary = '';
  if (total <= 0 && (done == null || done <= 0)) {
    primary = '';
  } else if (done != null && total > 0 && done !== total) {
    primary = `${done}/${total} ${unit}`;
  } else if (total > 0) {
    primary = `共 ${total} ${unit}`;
  } else if (done != null && done > 0) {
    primary = `${done} ${unit}`;
  }
  const secN = Number(metric.secondaryCount) || 0;
  const secLabel = String(metric.secondaryLabel || '').trim();
  const skipPrimary = Boolean(pressure?.label && pressure.label === unit);
  const skipSecondary = Boolean(pressure?.label && secLabel && pressure.label === secLabel);
  const secondary = !skipSecondary && secN > 0 && secLabel ? `${secLabel} ${secN}` : '';
  const extra = String(metric.extraLabel || '').trim();
  const pressureBit = pressure && pressure.label && pressure.target > 0
    ? `${pressure.label} ${pressure.actual}/${pressure.target}`
    : '';
  const pressureNote = pressure?.needsManual ? '需手动再试' : '';
  return [pressureBit, skipPrimary ? '' : primary, secondary, extra, pressureNote].filter(Boolean).join(' · ');
}

function phasesFromDetail(detail: {
  phases?: FactoryBatchPhase[];
  facets?: Array<{ jobType: string; status: string; progressPct?: number; progressLabel?: string; wired?: boolean }>;
}): FactoryBatchPhase[] {
  const raw = detail.phases?.length
    ? detail.phases
    : (detail.facets || []).map((f) => ({
      phase: f.jobType,
      status: f.status === 'planned' ? 'pending' : f.status,
      progressPct: f.progressPct,
      progressLabel: f.progressLabel,
      wired: f.wired,
    }));
  // enrich 已并入颗粒提取，不单独展示
  return raw.filter((p) => p.phase !== 'enrich');
}

function displayPhaseName(phase: string, enableSemanticSplit: boolean): string {
  if (phase === 'layout' && !enableSemanticSplit) return '页图准备';
  return phaseLabel[phase] || phase;
}

function resolveContinueFromPhase(
  phases: FactoryBatchPhase[],
  currentPhase: string | null | undefined,
  batch: FactoryBatch | null | undefined,
  metrics?: FactoryPhaseMetric[],
): string | null {
  const incomplete = phases.find((p) =>
    p.phase !== 'figure_extract'
    && p.status !== 'not_wired'
    && p.status !== 'skipped_optional'
    && !isTerminalOk(p.status)
  );
  if (incomplete) return incomplete.phase;
  for (const phase of ['extract', 'ttq', 'ipttq'] as const) {
    if (resolvePhasePressure(batch, metricForPhase(metrics || [], phase), phase)?.short) {
      return phase;
    }
  }
  if (currentPhase && currentPhase !== 'figure_extract') return currentPhase;
  return null;
}

function isTerminalOk(status: string): boolean {
  return (
    status === 'succeeded'
    || status === 'skipped_unsupported_discipline'
    || status === 'skipped_optional'
  );
}

function isLiveStatus(status: string): boolean {
  return status === 'queued' || status === 'running';
}

/** Match AppFactory FACTORY_STALL_MS default (10 min). */
const FACTORY_STALL_MS = 10 * 60 * 1000;

function isStalledJob(job: FactoryJob, nowMs: number): boolean {
  if (job.status !== 'queued' && job.status !== 'running') return false;
  const updated = parseTimestamp(job.updatedAt) ?? parseTimestamp(job.createdAt);
  if (updated == null) return false;
  return nowMs - updated >= FACTORY_STALL_MS;
}

function clampPct(value: number | null | undefined): number {
  if (value == null || Number.isNaN(value)) return 0;
  return Math.min(100, Math.max(0, Math.round(value)));
}

/** Parse worker extras like ·等FC30s·超时2·跳过12 so skip/timeout aren't hidden. */
function ipttqStockWasteBits(rest: string): string {
  const bits: string[] = [];
  const wait = /·等FC(\d+s)/.exec(rest);
  const to = /·超时(\d+)/.exec(rest);
  const sk = /·跳过(\d+)/.exec(rest);
  if (wait) bits.push(`等 FC ${wait[1]}`);
  if (to && Number(to[1]) > 0) bits.push(`超时 ${to[1]}`);
  if (sk && Number(sk[1]) > 0) bits.push(`跳过 ${sk[1]}`);
  return bits.length ? ` · ${bits.join(' · ')}` : '';
}

/**
 * Derive phase % from counters in progressLabel so the bar tracks the same
 * fractions the user sees (e.g. 补 RFE/PAP 4/31, 诱导已回 7/55).
 */
function deriveProgressPctFromLabel(raw: string | undefined | null, fallback: number): number {
  const key = String(raw || '').trim();
  if (!key) return clampPct(fallback);

  let m = /^enrich_heal:(\d+)\/(\d+)/.exec(key) || /^enrich\s+(\d+)\/(\d+)/.exec(key);
  if (m) {
    const done = Number(m[1]) || 0;
    const total = Math.max(1, Number(m[2]) || 1);
    return clampPct(90 + Math.round((done / total) * 9));
  }

  m = /^extract_live:(\d+)\/(\d+)/.exec(key);
  if (m) {
    const done = Number(m[1]) || 0;
    const total = Math.max(1, Number(m[2]) || 1);
    const inflight = Number(/进行中(\d+)/.exec(key)?.[1]) || 0;
    return clampPct(Math.min(95, 5 + Math.round(((done + inflight * 0.35) / total) * 90)));
  }

  m = /^extract_page:(\d+)\/(\d+)/.exec(key);
  if (m) {
    const done = Number(m[1]) || 0;
    const total = Math.max(1, Number(m[2]) || 1);
    return clampPct(Math.min(95, 5 + Math.round((done / total) * 90)));
  }

  m = /^ipttq_product_stock:(\d+)\/(\d+)(?::bank(\d+)\/(\d+))?/.exec(key);
  if (m) {
    const groupsDone = Number(m[1]) || 0;
    const groupsTotal = Math.max(1, Number(m[2]) || 1);
    const bankDone = m[3] != null ? Number(m[3]) || 0 : 0;
    const bankTarget = m[4] != null ? Math.max(1, Number(m[4]) || 1) : 1;
    const partial = m[3] != null ? bankDone / bankTarget : 0;
    return clampPct(Math.min(96, 8 + Math.round(((groupsDone + partial) / groupsTotal) * 88)));
  }

  m = /^ipttq_condense:(\d+)\/(\d+)(?::g\d+)?(?::(?:skill|done|bank(\d+)\/(\d+)))?/.exec(key);
  if (m) {
    const groupsDone = Number(m[1]) || 0;
    const groupsTotal = Math.max(1, Number(m[2]) || 1);
    const bankDone = m[3] != null ? Number(m[3]) || 0 : 0;
    const bankTarget = m[4] != null ? Math.max(1, Number(m[4]) || 1) : 1;
    const partial = m[3] != null ? bankDone / bankTarget : 0;
    return clampPct(Math.min(95, 5 + Math.round(((groupsDone + partial * 0.4) / groupsTotal) * 90)));
  }

  m = /^ttq_product_resolve:(\d+)\/(\d+)/.exec(key);
  if (m) {
    const done = Number(m[1]) || 0;
    const total = Math.max(1, Number(m[2]) || 1);
    return clampPct(Math.min(92, 55 + Math.round((done / total) * 37)));
  }

  m = /^ttq_title_condense:(\d+)\/(\d+)/.exec(key);
  if (m) {
    const done = Number(m[1]) || 0;
    const total = Math.max(1, Number(m[2]) || 1);
    return clampPct(Math.min(55, 12 + Math.round((done / total) * 40)));
  }

  m = /(\d+)\s*\/\s*(\d+)\s*页/.exec(key);
  if (m) {
    const done = Number(m[1]) || 0;
    const total = Math.max(1, Number(m[2]) || 1);
    return clampPct(Math.min(95, 5 + Math.round((done / total) * 90)));
  }

  return clampPct(fallback);
}

function formatElapsed(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function parseTimestamp(value: unknown): number | null {
  const t = Date.parse(String(value || ''));
  return Number.isFinite(t) ? t : null;
}

/** Jobs belonging to the current pipeline run (after last full restart). */
function jobsInCurrentRun(jobs: FactoryJob[], runStartedAt: number | null): FactoryJob[] {
  if (runStartedAt == null) return jobs;
  const skewMs = 5_000;
  return jobs.filter((j) => {
    const t = parseTimestamp(j.createdAt);
    return t != null && t >= runStartedAt - skewMs;
  });
}

/**
 * Start of the current wall-clock run.
 * Prefer pipelineRestartedAt, and always honor the newest layout/ingest job
 * (full restart re-enqueues layout) so pre-restart job history cannot inflate the timer.
 */
function resolvePipelineRunStartedAt(params: {
  batch: FactoryBatch | null;
  jobs: FactoryJob[];
  clientStartedAt: number | null;
}): number | null {
  const { batch, jobs, clientStartedAt } = params;
  const restarted = parseTimestamp(batch?.pipelineRestartedAt);
  const layoutStarts = jobs
    .filter((j) => j.jobType === 'layout' || j.jobType === 'ingest')
    .map((j) => parseTimestamp(j.createdAt))
    .filter((t): t is number => t != null);
  const newestLayout = layoutStarts.length ? Math.max(...layoutStarts) : null;

  const anchors = [restarted, newestLayout].filter((t): t is number => t != null);
  if (anchors.length) return Math.max(...anchors);

  const anyCreated = jobs
    .map((j) => parseTimestamp(j.createdAt))
    .filter((t): t is number => t != null);
  if (anyCreated.length) return Math.min(...anyCreated);

  return clientStartedAt ?? parseTimestamp(batch?.updatedAt) ?? null;
}

/**
 * Pipeline wall-clock for the *current* run only.
 * When idle, freeze at latest job updatedAt in this run so completed batches don't keep growing.
 * When paused, freeze at pipelinePausedAt — pause time must not inflate「已用时」.
 */
function resolvePipelineElapsedMs(params: {
  batch: FactoryBatch | null;
  jobs: FactoryJob[];
  now: number;
  live: boolean;
  clientStartedAt: number | null;
}): number {
  const { batch, jobs, now, live, clientStartedAt } = params;
  const start = resolvePipelineRunStartedAt({ batch, jobs, clientStartedAt });
  if (start == null) return 0;

  const runJobs = jobsInCurrentRun(jobs, start);
  const jobUpdated = runJobs
    .map((j) => parseTimestamp(j.updatedAt))
    .filter((t): t is number => t != null);
  const pausedAt = batch?.pipelinePaused
    ? parseTimestamp(batch.pipelinePausedAt)
    : null;

  let end: number;
  if (batch?.pipelinePaused) {
    end = pausedAt
      ?? (jobUpdated.length ? Math.max(...jobUpdated) : null)
      ?? parseTimestamp(batch.updatedAt)
      ?? now;
  } else if (live) {
    end = now;
  } else if (jobUpdated.length) {
    end = Math.max(...jobUpdated);
  } else {
    end = clientStartedAt && clientStartedAt > start ? now : start;
  }

  return Math.max(0, end - start);
}

function resolveStageElapsedMs(params: {
  phase: string | null | undefined;
  jobs: FactoryJob[];
  now: number;
  live: boolean;
  clientStageStartedAt: number | null;
  runStartedAt: number | null;
  /** When set, freeze stage clock (pause must not keep growing「本步」). */
  freezeAt?: number | null;
}): number {
  const { phase, jobs, now, live, clientStageStartedAt, runStartedAt, freezeAt } = params;
  if (!phase) return 0;
  if (!live && freezeAt == null) return 0;
  const jobType = PHASE_JOB_TYPE[phase] || phase;
  const runJobs = jobsInCurrentRun(jobs, runStartedAt);
  const job =
    runJobs.find((j) => j.jobType === jobType && (j.status === 'running' || j.status === 'queued'))
    || runJobs.find((j) => j.jobType === jobType)
    || null;
  const start =
    parseTimestamp(job?.createdAt)
    ?? clientStageStartedAt
    ?? null;
  if (start == null) return 0;
  const clampedStart = runStartedAt != null ? Math.max(start, runStartedAt) : start;
  const end = freezeAt != null ? freezeAt : now;
  return Math.max(0, end - clampedStart);
}

function humanProgressLabel(raw: string | undefined | null): string {
  const key = String(raw || '').trim();
  if (!key) return '';
  if (PROGRESS_LABEL_ZH[key]) return PROGRESS_LABEL_ZH[key];
  if (key.startsWith('semantic_split_seam:')) {
    return `接缝判定 · ${key.slice('semantic_split_seam:'.length)}`;
  }
  if (key.startsWith('semantic_split_raster:')) {
    return `渲页图 · ${key.slice('semantic_split_raster:'.length)}`;
  }
  if (key.startsWith('semantic_split_page:')) {
    return `跨页切分 · ${key.slice('semantic_split_page:'.length)}`;
  }
  if (key.startsWith('page_direct_raster:')) {
    return `转图 FC · ${key.slice('page_direct_raster:'.length)}`;
  }
  if (key.startsWith('page_direct_prep:')) {
    return `页图准备 · ${key.slice('page_direct_prep:'.length)}`;
  }
  if (key.startsWith('page_direct_oss:')) {
    return `下载源 PDF · ${key.slice('page_direct_oss:'.length)}`;
  }
  if (key.startsWith('page_direct_pdf:')) {
    return `解析 PDF · ${key.slice('page_direct_pdf:'.length)}`;
  }
  if (key.startsWith('ttq_title_condense:')) {
    return `浓缩组标题 · ${key.slice('ttq_title_condense:'.length)}`;
  }
  if (key.startsWith('ttq_similarity_readmit:')) {
    const rest = key.slice('ttq_similarity_readmit:'.length);
    const m = /^(\d+)\/(\d+)(?::([^·]+))?(?:·(.*))?/i.exec(rest);
    if (m) {
      const title = m[4] ? ` · ${m[4]}` : '';
      const ids = m[3] ? ` · 在判 ${m[3]}` : '';
      return `相似再入围 · ${m[1]}/${m[2]}${ids}${title}`;
    }
    return `相似再入围 · ${rest}`;
  }
  if (key.startsWith('ttq_product_resolve:')) {
    const rest = key.slice('ttq_product_resolve:'.length);
    const live = /^(\d+)\/(\d+)(?:·进行中(\d+))?$/.exec(rest);
    if (live?.[3]) {
      return `写入成品智能体 · ${live[1]}/${live[2]} 组 · 进行中 ${live[3]}`;
    }
    return `写入成品智能体 · ${rest} 组`;
  }
  if (key.startsWith('ttq_product_bank_stock:')) {
    return `写入成品智能体 · ${key.slice('ttq_product_bank_stock:'.length)} 组`;
  }
  if (key.startsWith('ipttq_product_stock:')) {
    const rest = key.slice('ipttq_product_stock:'.length);
    // New aggregate: 1/13:bank3/55·进行中5·活跃3组  or  ...:bank3/55·进行中5:g6
    const agg = /^(\d+)\/(\d+)(?::bank(\d+)\/(\d+)(?:·进行中(\d+))?(?:·活跃(\d+)组)?(?::g(\d+))?)?(?::g(\d+):?(skip|done)?)?/.exec(rest);
    if (agg && agg[3] != null) {
      const head = `已完成 ${agg[1]}/${agg[2]} 组`;
      const bank = `诱导已回 ${agg[3]}/${agg[4]}`;
      const inflight = agg[5] ? ` · FC 在途 ${agg[5]}` : '';
      const active = agg[6] ? ` · 活跃 ${agg[6]} 组` : '';
      const focus = agg[7] ? ` · 焦点第 ${agg[7]} 组` : '';
      return `品题浓缩 · ${head} · ${bank}${inflight}${active}${focus}${ipttqStockWasteBits(rest)}`;
    }
    const m = /^(\d+)\/(\d+)(?::g(\d+))?(?::(skip|done|bank(\d+)\/(\d+)(?:·进行中(\d+))?))?/.exec(rest);
    if (m) {
      const head = `已完成 ${m[1]}/${m[2]} 组`;
      if (m[4] === 'skip' && m[3]) return `品题浓缩 · ${head} · 第 ${m[3]} 组题库已满`;
      if (m[4] === 'done' && m[3]) return `品题浓缩 · ${head} · 第 ${m[3]} 组完成`;
      if (m[5] != null && m[3]) {
        const inflight = m[7] ? ` · FC 在途 ${m[7]}` : '';
        return `品题浓缩 · ${head} · 第 ${m[3]} 组已回 ${m[5]}/${m[6]}${inflight}${ipttqStockWasteBits(rest)}`;
      }
      if (m[3]) return `品题浓缩 · ${head} · 正在处理第 ${m[3]} 组`;
      return `品题浓缩 · 生成诱导题库 · ${head}`;
    }
    return `品题浓缩 · 生成诱导题库 · ${rest}`;
  }
  if (key.startsWith('ipttq_condense:')) {
    const rest = key.slice('ipttq_condense:'.length);
    const m = /^(\d+)\/(\d+)(?::g(\d+))?(?::(skill|done|bank(\d+)\/(\d+)))?/.exec(rest);
    if (m) {
      const head = `${m[1]}/${m[2]} 组`;
      if (m[4] === 'skill' && m[3]) return `品题浓缩 · ${head} · 第 ${m[3]} 组 skill`;
      if (m[4] === 'done' && m[3]) return `品题浓缩 · ${head} · 第 ${m[3]} 组完成`;
      if (m[5] != null && m[3]) return `品题浓缩 · ${head} · 第 ${m[3]} 组诱导 ${m[5]}/${m[6]}`;
      if (m[5] != null) return `品题浓缩 · ${head} · 诱导 ${m[5]}/${m[6]}`;
      return `品题浓缩 · ${head}`;
    }
    return `品题浓缩 · ${rest}`;
  }
  if (key.startsWith('enrich_heal:') || /^enrich\s+\d/.test(key)) {
    const rest = key.startsWith('enrich_heal:')
      ? key.slice('enrich_heal:'.length)
      : key.replace(/^enrich\s+/, '');
    const m = /^(\d+)\/(\d+)(?::book(\d+))?(?::extractOk(\d+))?(?::extractFail(\d+))?(?::ready(\d+))?/.exec(rest);
    if (m) {
      const heal = `${m[1]}/${m[2]}`;
      const book = m[3] ? Number(m[3]) : 0;
      const extractOk = m[4] != null ? Number(m[4]) : null;
      const extractFail = m[5] != null ? Number(m[5]) : null;
      const ready = m[6] != null ? Number(m[6]) : null;
      // 补 RFE/PAP first — this fraction drives the progress bar.
      const parts: string[] = [`补 RFE/PAP ${heal}`];
      if (book > 0 && ready != null) {
        parts.push(`全书就绪 ${ready}/${book}`);
      } else if (book > 0) {
        parts.push(`全书 ${book} 页`);
      }
      if (extractOk != null) {
        parts.push(
          extractFail && extractFail > 0
            ? `提取成功 ${extractOk}（失败 ${extractFail}）`
            : `提取成功 ${extractOk}`,
        );
      }
      return `颗粒提取收尾 · ${parts.join(' · ')}`;
    }
    return `颗粒提取收尾 · 补 RFE/PAP ${rest}`;
  }
  if (key.startsWith('extract_live:')) {
    const rest = key.slice('extract_live:'.length);
    return `逐页提取 · ${rest}`;
  }
  if (key.startsWith('extract_page:')) {
    return `逐页提取 · ${key.slice('extract_page:'.length)}`;
  }
  if (key.startsWith('extract_fc_error:')) {
    return `提取 FC 报错 · ${key.slice('extract_fc_error:'.length)}`;
  }
  if (key.startsWith('extract_calling:')) {
    return `调用提取 FC · ${key.slice('extract_calling:'.length)}`;
  }
  if (key.startsWith('extract_enrich:')) {
    return `本页 RFE/PAP · ${key.slice('extract_enrich:'.length)}`;
  }
  if (key.startsWith('extract_render:')) {
    return `渲染页图 · ${key.slice('extract_render:'.length)}`;
  }
  if (key.startsWith('extract_math_extra:')) {
    return `数学提取 · ${key.slice('extract_math_extra:'.length)}`;
  }
  if (key.startsWith('counting_pages:')) {
    return `统计页数 · ${key.slice('counting_pages:'.length)}`;
  }
  if (key.startsWith('upload_booklet:')) {
    return `上传分册 · ${key.slice('upload_booklet:'.length)}`;
  }
  if (key.startsWith('mineru:')) {
    return `版面解析 · ${key.slice('mineru:'.length)}`;
  }
  if (key.startsWith('job_type_not_implemented:')) {
    return `工序未接入 · ${key.slice('job_type_not_implemented:'.length)}`;
  }
  // Never surface raw snake_case / FC-ish keys to desk users.
  if (/^[a-z][a-z0-9_]*$/.test(key)) return '处理中…';
  return key;
}

function resolvePhaseProgress(
  phase: FactoryBatchPhase,
  jobs: FactoryJob[],
  workerQueue?: FactoryWorkerQueueStatus | null,
): { pct: number; label: string; job: FactoryJob | null } {
  const jobType = PHASE_JOB_TYPE[phase.phase] || phase.phase;
  let job =
    jobs.find((j) => j.jobType === jobType && (j.status === 'running' || j.status === 'queued'))
    || jobs.find((j) => j.jobType === jobType)
    || null;
  // 颗粒提取收尾走 enrich job；进度/心跳应以 enrich 为准，勿拿 extract 的「部分页失败 x/101」当当前分母。
  const enrichDriving =
    phase.phase === 'extract'
    && (
      String(phase.progressLabel || '').startsWith('enrich')
      || jobs.some((j) => j.jobType === 'enrich' && (j.status === 'running' || j.status === 'queued'))
    );
  if (enrichDriving) {
    const enrichJob =
      jobs.find((j) => j.jobType === 'enrich' && (j.status === 'running' || j.status === 'queued'))
      || jobs.find((j) => j.jobType === 'enrich')
      || null;
    if (enrichJob) job = enrichJob;
  }
  const rawLabel = phase.progressLabel || job?.progressLabel || '';
  const storedPct = clampPct(
    phase.progressPct != null ? phase.progressPct : job?.progressPct,
  );
  const pct = deriveProgressPctFromLabel(rawLabel, storedPct);
  let label =
    humanProgressLabel(phase.progressLabel)
    || humanProgressLabel(job?.progressLabel)
    || phaseStatusLabel[phase.status]
    || phase.status;
  // Queued ≠ worker offline: use the matching lane (prep vs pipeline), not a shared pool.
  if (
    (phase.status === 'queued' || job?.status === 'queued' || rawLabel === 'queued')
    && workerQueue?.alive
    && workerQueue.enabled !== false
  ) {
    const isPrepPhase = ['layout', 'semantic_split', 'structure', 'ingest'].includes(phase.phase)
      || ['layout', 'ingest', 'semantic_split', 'aggregate'].includes(jobType);
    const prepTypes = workerQueue.prepJobTypes || ['ingest', 'layout', 'semantic_split', 'aggregate'];
    const pipeTypes = workerQueue.pipelineJobTypes || ['extract', 'enrich', 'ttq', 'ipttq'];
    const cap = Math.max(
      1,
      Number(
        isPrepPhase
          ? (workerQueue.prepJobSlotCapacity ?? workerQueue.jobSlotCapacity)
          : (workerQueue.pipelineJobSlotCapacity ?? workerQueue.jobSlotCapacity),
      ) || 1,
    );
    const busy = Math.min(
      cap,
      Math.max(
        0,
        Number(
          isPrepPhase
            ? (workerQueue.prepJobSlotsBusy ?? 0)
            : (workerQueue.pipelineJobSlotsBusy ?? 0),
        ) || 0,
      ),
    );
    const laneQueued = (() => {
      const by = workerQueue.byTypeQueued || {};
      const types = isPrepPhase ? prepTypes : pipeTypes;
      return types.reduce((n, t) => n + Math.max(0, Number(by[t]) || 0), 0);
    })();
    const laneName = isPrepPhase ? '页图准备槽' : '提取/品题槽';
    if (busy >= cap) {
      label =
        `等待${laneName}空闲 ${busy}/${cap}`
        + ` · 本队列约 ${laneQueued} 个排队`
        + (isPrepPhase ? ' · 领取后才会调页图转图 FC' : '');
    } else if (laneQueued > 1) {
      label =
        `已排队（本队列约 ${laneQueued}）· ${laneName}空闲 ${cap - busy}/${cap}`
        + (isPrepPhase ? ' · 领取后才会调页图转图 FC' : '');
    } else {
      label =
        `已排队 · ${laneName} ${busy}/${cap}`
        + (isPrepPhase ? ' · 即将领取并调转图 FC' : ' · 即将领取');
    }
  } else if (
    (phase.status === 'queued' || job?.status === 'queued' || rawLabel === 'queued')
    && workerQueue
    && (workerQueue.enabled === false || !workerQueue.alive)
  ) {
    label = workerQueue.enabled === false
      ? '已排队，但厂工已关闭（ENABLED=0），不会领取'
      : '已排队，但厂工离线，不会领取';
  }
  return { pct, label, job };
}

function overallPipelinePct(
  phases: FactoryBatchPhase[],
  jobs: FactoryJob[],
  workerQueue?: FactoryWorkerQueueStatus | null,
  batch?: FactoryBatch | null,
  metrics?: FactoryPhaseMetric[],
): number {
  if (!phases.length) return 0;
  const pressureShort = batchPressureShort(batch, metrics);
  if (phases.every((p) => isTerminalOk(p.status)) && !pressureShort) return 100;
  let sum = 0;
  for (const p of phases) {
    if (isTerminalOk(p.status)) {
      sum += 100;
      continue;
    }
    if (p.status === 'failed') {
      sum += clampPct(resolvePhaseProgress(p, jobs, workerQueue).pct);
      continue;
    }
    if (isLiveStatus(p.status)) {
      const live = resolvePhaseProgress(p, jobs, workerQueue).pct;
      sum += live > 0 ? Math.min(99, live) : p.status === 'queued' ? 5 : 15;
      continue;
    }
    // Current but not live (not_wired / awaiting_discipline / blocked…)
    if (p.status === 'blocked_by_auth') sum += 2;
    else if (p.status === 'not_wired' || p.status === 'awaiting_discipline') sum += 8;
  }
  const avg = clampPct(sum / phases.length);
  // Job statuses can all be succeeded while induction-bank backpressure is still short.
  if (pressureShort) return Math.min(99, avg);
  return avg;
}

export function FactoryTaskDetailPage() {
  const { batchId = '' } = useParams();
  const navigate = useNavigate();
  const { workerQueue = null } = useOutletContext<FactoryDeskOutletContext>() ?? {};
  const { setActiveOrderId } = useFactoryOrderScope();
  const [batch, setBatch] = useState<FactoryBatch | null>(null);
  const [jobs, setJobs] = useState<FactoryJob[]>([]);
  const [phases, setPhases] = useState<FactoryBatchPhase[]>([]);
  const [currentPhase, setCurrentPhase] = useState<string | null>(null);
  const [artifactAccess, setArtifactAccess] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionConfirm, setActionConfirm] = useState<
    'continue' | 'continue_pipeline' | 'reextract_all' | 'restart' | 'delete' | null
  >(null);
  const [enableSemanticSplit, setEnableSemanticSplit] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [dataRefreshedAt, setDataRefreshedAt] = useState<number | null>(null);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [headerPhase, setHeaderPhase] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [idCopied, setIdCopied] = useState(false);
  const [detailBusy, setDetailBusy] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailUsage, setDetailUsage] = useState<{
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    byPhase: Record<string, {
      promptTokens: number;
      completionTokens: number;
      totalTokens: number;
    }>;
    phaseMetrics?: FactoryPhaseMetric[];
    notes: string[];
  } | null>(null);
  const [phaseMetrics, setPhaseMetrics] = useState<FactoryPhaseMetric[]>([]);
  const moreMenuRef = useRef<HTMLDivElement | null>(null);

  const pipelineStartedAt = useRef<number | null>(null);
  const stageStartedAt = useRef<number | null>(null);
  const stageKeyRef = useRef<string>('');

  const reload = useCallback(async (opts?: { includeUsage?: boolean }) => {
    const detail = await factoryService.getBatch(batchId);
    setBatch(detail.batch);
    if (detail.batch?.orderId) setActiveOrderId(detail.batch.orderId);
    setEnableSemanticSplit(Boolean(detail.batch?.enableSemanticSplit));
    setJobs(detail.jobs || []);
    const nextPhases = phasesFromDetail(detail);
    setPhases(nextPhases);
    const nextCurrent =
      detail.currentPhase
      || nextPhases.find((p) => !isTerminalOk(p.status))?.phase
      || null;
    setCurrentPhase(nextCurrent);
    setArtifactAccess(detail.artifactAccess !== false);
    setMessage(detail.message || null);

    if (opts?.includeUsage) {
      const usageRes = await factoryService.getBatchUsage(batchId).catch(() => null);
      if (usageRes?.usage) {
        setPhaseMetrics(usageRes.usage.phaseMetrics || []);
        setDetailUsage(usageRes.usage);
      }
    }

    // Current-run watermark only — never min(all historical jobs).
    pipelineStartedAt.current = resolvePipelineRunStartedAt({
      batch: detail.batch,
      jobs: detail.jobs || [],
      clientStartedAt: pipelineStartedAt.current,
    });
    setDataRefreshedAt(Date.now());
  }, [batchId, setActiveOrderId]);

  const livePhase = useMemo(
    () => phases.find((p) => p.phase === currentPhase) || null,
    [phases, currentPhase],
  );
  const runStartedAt = resolvePipelineRunStartedAt({
    batch,
    jobs,
    clientStartedAt: pipelineStartedAt.current,
  });
  const pipelineComplete = phases.length > 0
    && phases.every((p) => isTerminalOk(p.status))
    && !batchPressureShort(batch, phaseMetrics)
    && batch?.status === 'ready';
  const inFlight = useMemo(() => {
    // All visible phases done → never keep spinning / wall-clock growing.
    if (pipelineComplete) return false;
    if (livePhase && isLiveStatus(livePhase.status)) return true;
    // Only latest job per type in the *current* run (ignore stale historical queued/running).
    const runJobs = jobsInCurrentRun(jobs, runStartedAt);
    const latestByType = new Map<string, FactoryJob>();
    for (const j of runJobs) {
      if (!latestByType.has(j.jobType)) latestByType.set(j.jobType, j);
    }
    return [...latestByType.values()].some(
      (j) => j.status === 'queued' || j.status === 'running',
    );
  }, [pipelineComplete, livePhase, jobs, runStartedAt]);

  useEffect(() => {
    const key = `${livePhase?.phase || ''}:${livePhase?.status || ''}`;
    if (key !== stageKeyRef.current) {
      stageKeyRef.current = key;
      stageStartedAt.current = Date.now();
    }
  }, [livePhase?.phase, livePhase?.status]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // First paint: progress + usage once. Polling below skips usage (was multi‑MB + slow).
        await reload({ includeUsage: true });
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  useEffect(() => {
    // Poll AppFactory only (factoryService → appFactoryBaseUrl). Never AppFunctor/FC.
    // In-flight: ~1.5s; idle: gentler. Usage refreshed separately (expensive).
    const timer = window.setInterval(() => {
      void reload().catch(() => undefined);
    }, inFlight ? 1500 : 5000);
    return () => window.clearInterval(timer);
  }, [reload, inFlight]);

  useEffect(() => {
    // Token / 均耗: slow cadence — /usage used to pull full structural unit blobs.
    const timer = window.setInterval(() => {
      void factoryService.getBatchUsage(batchId).then((res) => {
        if (res?.usage) {
          setPhaseMetrics(res.usage.phaseMetrics || []);
          setDetailUsage(res.usage);
        }
      }).catch(() => undefined);
    }, inFlight ? 20_000 : 60_000);
    return () => window.clearInterval(timer);
  }, [batchId, inFlight]);

  useEffect(() => {
    // Always tick while page open so「N秒前刷新」keeps moving even when idle.
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  async function persistSemanticSplitOption(next: boolean) {
    setEnableSemanticSplit(next);
    setBusy(true);
    setError(null);
    try {
      const updated = await factoryService.setBatchPipelineOptions(batchId, {
        enableSemanticSplit: next,
      });
      setBatch(updated);
      setEnableSemanticSplit(Boolean(updated.enableSemanticSplit));
      await reload();
    } catch (err) {
      setEnableSemanticSplit((v) => !v);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onEnqueueLayout() {
    setBusy(true);
    setError(null);
    try {
      await factoryService.setBatchPipelineOptions(batchId, { enableSemanticSplit });
      await factoryService.enqueueLayout(batchId);
      pipelineStartedAt.current = Date.now();
      stageStartedAt.current = Date.now();
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!moreMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!moreMenuRef.current?.contains(e.target as Node)) setMoreMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoreMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [moreMenuOpen]);

  async function confirmRestartPipeline() {
    setActionConfirm(null);
    setBusy(true);
    setError(null);
    try {
      const started = Date.now();
      pipelineStartedAt.current = started;
      stageStartedAt.current = started;
      setNow(started);
      await factoryService.restartPipeline(batchId, { enableSemanticSplit });
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDeleteBatch() {
    setActionConfirm(null);
    setBusy(true);
    setError(null);
    try {
      await factoryService.deleteBatch(batchId);
      navigate('/factory/tasks', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  async function confirmAction() {
    const kind = actionConfirm;
    if (!kind) return;
    if (kind === 'restart') {
      await confirmRestartPipeline();
      return;
    }
    if (kind === 'delete') {
      await confirmDeleteBatch();
      return;
    }
    if (kind === 'continue_pipeline') {
      setActionConfirm(null);
      await onContinuePipeline();
      return;
    }
    setActionConfirm(null);
    await onContinueExtract(kind === 'reextract_all');
  }

  async function onContinueExtract(forceRedo = false) {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await factoryService.continueExtract(batchId, { forceRedo });
      setMessage(
        forceRedo
          ? '已排队按当前学科策略重抽全部页（保留页单元，只清旧提取结果）。'
          : '已排队继续提取；已成功的页会跳过，不会清空。',
      );
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onContinuePipeline() {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const res = await factoryService.continuePipeline(batchId);
      setMessage(res.note || `已从检查点继续：${res.phaseLabel || res.phase}`);
      await reload({ includeUsage: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      // Stale "全部完成" vs live backpressure — refresh meters so the phase row matches.
      await reload({ includeUsage: true }).catch(() => undefined);
    } finally {
      setBusy(false);
    }
  }

  async function onPausePipeline() {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await factoryService.pausePipeline(batchId);
      setMessage('已请求暂停：在途调用结束后不再开新并发（不影响其它任务）。');
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onResumePipeline() {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await factoryService.resumePipeline(batchId);
      setMessage('已恢复，将继续未完成工序。');
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  function statusLabelForPhase(p: FactoryBatchPhase, index: number, all: FactoryBatchPhase[]) {
    if (batch?.pipelinePaused && (p.status === 'running' || p.status === 'queued')) {
      if (p.status === 'running' || (p.progressLabel && p.progressLabel !== 'paused')) {
        return '暂停中·在途收尾';
      }
      return '已暂停';
    }
    if (p.phase === 'semantic_split' && p.status === 'skipped_optional') {
      return '单页';
    }
    if (p.phase === 'structure' && p.status === 'skipped_optional') {
      return '跳过·单页直通';
    }
    if (p.phase === 'semantic_split' && p.status === 'succeeded' && enableSemanticSplit) {
      return '跨页完成';
    }
    if (p.status === 'pending') {
      const prev = index > 0 ? all[index - 1] : null;
      if (!prev || isTerminalOk(prev.status) || prev.status === 'not_wired') {
        return p.phase === 'semantic_split' && enableSemanticSplit ? '待跨页分析' : '待执行';
      }
      return '等待前序';
    }
    if (isTerminalOk(p.status)) {
      const pressure = resolvePhasePressure(batch, metricForPhase(phaseMetrics, p.phase), p.phase);
      if (pressure?.needsManual) return '异常·需手动再试';
      if (pressure?.short) {
        const jobType = PHASE_JOB_TYPE[p.phase] || p.phase;
        const moving = isLiveStatus(p.status)
          || jobs.some((j) => j.jobType === jobType && isLiveStatus(j.status))
          || (p.phase === 'extract' && jobs.some((j) => j.jobType === 'enrich' && isLiveStatus(j.status)));
        if (moving) return '背压补齐中';
        if (batch?.pipelinePaused) return '背压未达标·已暂停';
        return '背压未达标·未在跑';
      }
    }
    return phaseStatusLabel[p.status] || p.status;
  }

  function hintForPhase(phase: string): string {
    if (phase === 'layout') {
      return enableSemanticSplit
        ? '文档块切分（后续工序的基础）；完成后采样判定学科'
        : '单页直通：每页整图准备（转图 FC 遇网络失败自动重试最多 3 次）；完成后判定学科（数学走 math-k12-extra）';
    }
    if (phase === 'semantic_split') {
      return enableSemanticSplit
        ? '跨页 · layout 之后对页界文档块做接缝判定（有图先转文本）'
        : '单页 · 未开跨页分析，本步瞬时完成';
    }
    if (phase === 'structure') {
      return enableSemanticSplit
        ? '跨页模式：文档块 + 接缝合并'
        : '单页直通：跳过结构聚合（layout 已按页建单元）';
    }
    if (phase === 'extract') {
      if (batch?.disciplineStatus === 'insufficient_sample') {
        return enableSemanticSplit
          ? '文档块采样不足，请重新判定学科后再提取'
          : '学科样本不足，请重新判定后再提取';
      }
      if (batch?.extractEnabled === false && batch?.primaryDiscipline && batch.primaryDiscipline !== 'unknown') {
        return `学科「${disciplineLabel[batch.primaryDiscipline] || batch.primaryDiscipline}」暂未开放颗粒提取`;
      }
      if (batch?.primaryDiscipline === 'unknown' || batch?.primaryDiscipline == null) {
      return enableSemanticSplit
        ? '等待学科判定（全文文档块随机采样）'
        : '等待学科判定（页图 select-modality，最多 3 次）';
      }
      if (batch?.primaryDiscipline === 'math') {
        return enableSemanticSplit
          ? '按结构单元提取（数学 → math-k12-extra，含 RFE/PAP）'
          : '单页直通：每页整图 → math-k12-extra（含 RFE/PAP）';
      }
      return enableSemanticSplit
        ? '按结构单元块流提取（非数学过渡 → extras_v2）'
        : '单页直通：每页整图 → extras_v2（非数学过渡）';
    }
    return phaseHint[phase] || '';
  }

  async function onRejudgeDiscipline() {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const res = await factoryService.judgeDiscipline(batchId, { maxBlocks: 32 });
      await reload();
      const enqueued = res.enqueued || [];
      if (enqueued.includes('extract')) {
        setMessage('学科判定成功，已继续排队颗粒提取。');
      } else if (res.discipline?.extractEnabled === true) {
        setMessage('学科判定成功；若提取未启动，请刷新或重新启动工序。');
      } else {
        setMessage(
          String(res.discipline?.reason || res.discipline?.resampleHint || '学科已更新。'),
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const progressPct = overallPipelinePct(phases, jobs, workerQueue, batch, phaseMetrics);
  const totalElapsed = resolvePipelineElapsedMs({
    batch,
    jobs,
    now,
    live: inFlight,
    clientStartedAt: pipelineStartedAt.current,
  });
  const pausedAtMs = (() => {
    if (!batch?.pipelinePaused) return null;
    const stamped = parseTimestamp(batch.pipelinePausedAt);
    if (stamped != null) return stamped;
    const jobTimes = jobs
      .map((j) => parseTimestamp(j.updatedAt))
      .filter((t): t is number => t != null);
    if (jobTimes.length) return Math.max(...jobTimes);
    return parseTimestamp(batch.updatedAt) ?? Date.now();
  })();
  const stageElapsed = resolveStageElapsedMs({
    phase: livePhase?.phase,
    jobs,
    now,
    live: inFlight,
    clientStageStartedAt: stageStartedAt.current,
    runStartedAt,
    freezeAt: pausedAtMs,
  });
  const list = phases.length
    ? phases
    : [
        { phase: 'layout', status: 'pending' },
        { phase: 'semantic_split', status: 'skipped_optional' },
        { phase: 'structure', status: 'pending' },
        { phase: 'extract', status: 'pending' },
        { phase: 'figure_extract', status: 'pending' },
        { phase: 'ttq', status: 'pending' },
        { phase: 'ipttq', status: 'pending' },
      ];

  /** Toggle decides which steps participate; skipped steps stay hidden. */
  const visibleList = useMemo(() => {
    return list
      .filter((p) => {
        if (!enableSemanticSplit && (p.phase === 'semantic_split' || p.phase === 'structure')) {
          return false;
        }
        if (p.status === 'skipped_optional') {
          // 勾选跨页后立刻露出将参与的工序（待重启后真正执行）。
          return enableSemanticSplit
            && (p.phase === 'semantic_split' || p.phase === 'structure');
        }
        return true;
      })
      .map((p) => (
        enableSemanticSplit
        && (p.phase === 'semantic_split' || p.phase === 'structure')
        && p.status === 'skipped_optional'
          ? { ...p, status: 'pending' }
          : p
      ));
  }, [list, enableSemanticSplit]);

  const layoutPhase = list.find((p) => p.phase === 'layout') || null;
  const extractPhase = list.find((p) => p.phase === 'extract') || null;
  const authFactory = batch?.processingAuthorization === 'factory';
  const ownerAccepted = Boolean(batch && isFactoryBatchOwnerAccepted(batch));
  const layoutDone = Boolean(layoutPhase && isTerminalOk(layoutPhase.status));
  const layoutLive = Boolean(layoutPhase && isLiveStatus(layoutPhase.status));
  const layoutFailed = layoutPhase?.status === 'failed';
  const layoutBlocked =
    layoutPhase?.status === 'blocked_by_auth'
    || batch?.processingAuthorization === 'awaiting_owner_auth';
  const pipelineLive = list.some((p) => isLiveStatus(p.status));
  const needsManualAttention = (() => {
    if (batch?.status === 'failed') return true;
    if (batchPressureNeedsManual(batch, phaseMetrics)) return true;
    if (batch?.meta?.ipttqBankBackpressureExhausted === true) return true;
    const bp = batch?.meta?.pipelineBackpressure;
    if (bp && typeof bp === 'object' && !Array.isArray(bp)) {
      return Object.values(bp as Record<string, { needsManual?: boolean }>).some(
        (row) => row?.needsManual === true,
      );
    }
    return false;
  })();
  const deskStatusText = (() => {
    if (!batch) return '…';
    if (needsManualAttention && !pipelineLive) return '失败 · 需手动再试';
    return factoryStatusLabel[batch.status] || batch.status;
  })();
  const extractLive = Boolean(extractPhase && isLiveStatus(extractPhase.status));
  const extractFailed = extractPhase?.status === 'failed';
  const extractFailedCount = Number(batch?.meta?.extractFailed ?? 0);
  const extractPressure = resolvePhasePressure(
    batch,
    metricForPhase(phaseMetrics, 'extract'),
    'extract',
  );
  const canContinueExtract =
    authFactory
    && !ownerAccepted
    && !pipelineLive
    && !batch?.pipelinePaused
    && Boolean(batch?.extractEnabled)
    && layoutDone
    && (extractFailed || extractFailedCount > 0 || Boolean(extractPressure?.short));
  const anyPhaseFailed = list.some((p) => p.status === 'failed');
  const runJobsForStall = jobsInCurrentRun(jobs, runStartedAt);
  const latestJobsByType = (() => {
    const map = new Map<string, FactoryJob>();
    for (const j of runJobsForStall) {
      if (!map.has(j.jobType)) map.set(j.jobType, j);
    }
    return [...map.values()];
  })();
  const pipelineStalled = latestJobsByType.some((j) => isStalledJob(j, now));
  const pipelineIncomplete = list.length > 0 && !pipelineComplete;
  const coldStartPending =
    !anyPhaseFailed
    && !pipelineStalled
    && !layoutFailed
    && Boolean(layoutPhase && layoutPhase.status === 'pending')
    && latestJobsByType.length === 0;
  /** Checkpoint continue: failed / stalled / idle incomplete (not healthy in-flight / not cold start). */
  const continueFromPhase = resolveContinueFromPhase(list, currentPhase, batch, phaseMetrics);
  const continueFromName = continueFromPhase
    ? displayPhaseName(continueFromPhase, enableSemanticSplit)
    : null;
  const canContinuePipeline =
    authFactory
    && !ownerAccepted
    && !layoutBlocked
    && !batch?.pipelinePaused
    && pipelineIncomplete
    && !coldStartPending
    && (
      anyPhaseFailed
      || pipelineStalled
      || batchPressureNeedsManual(batch, phaseMetrics)
      || batchPressureShort(batch, phaseMetrics)
      || (!pipelineLive && !layoutLive)
    );
  /**
   * Primary CTA for cold start / blocked / in-flight.
   * Layout failed → prefer「从检查点继续」(keep JPEGs); full restart remains available.
   */
  const layoutAction = (() => {
    if (ownerAccepted) return null;
    if (!layoutPhase) return null;
    if (layoutDone || layoutFailed) return null;
    if (layoutLive) {
      return { label: '工序进行中…', disabled: true, onClick: null as null };
    }
    if (layoutBlocked || !authFactory) {
      return { label: '待 Owner 授权', disabled: true, onClick: null as null };
    }
    return { label: '启动全部工序', disabled: busy, onClick: () => void onEnqueueLayout() };
  })();

  function openTaskDetailPanel() {
    setMoreMenuOpen(false);
    setDetailOpen(true);
    setDetailBusy(true);
    setDetailError(null);
    void (async () => {
      try {
        const res = await factoryService.getBatchUsage(batchId);
        setDetailUsage(res.usage);
        setPhaseMetrics(res.usage.phaseMetrics || []);
      } catch (err) {
        setDetailUsage(null);
        setDetailError(err instanceof Error ? err.message : String(err));
      } finally {
        setDetailBusy(false);
      }
    })();
  }

  const authLabel: Record<string, string> = {
    none: '未授权',
    awaiting_owner_auth: '待 Owner 授权',
    factory: '已授权工厂',
    owner_self: 'Owner 自处理',
    rejected: '已拒绝',
  };

  return (
    <div className="space-y-5">
      <div>
        <Link to="/factory/tasks" className="inline-flex items-center gap-1 text-sm text-muted hover:text-primary">
          <ArrowLeft size={16} />
          任务列表
        </Link>
        <div className="mt-2 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-semibold text-ink">{batch?.title || '任务详情'}</h1>
            <p className="mt-1 text-sm text-muted">
              {deskStatusText}
              {batch?.processingAuthorization
                ? ` · ${authLabel[batch.processingAuthorization] || batch.processingAuthorization}`
                : ''}
              {batch?.primaryDiscipline
                ? ` · 学科 ${disciplineLabel[batch.primaryDiscipline] || batch.primaryDiscipline}`
                : ''}
              {batch?.extractEnabled === false ? ' · 颗粒提取未开放' : ''}
              {ownerAccepted ? ' · 已验收' : ''}
              {batch?.pipelinePaused ? (pipelineLive ? ' · 暂停中（在途收尾）' : ' · 已暂停') : ''}
            </p>
            {batch?.disciplineNote ? (
              <p className="mt-1 text-xs text-muted">{batch.disciplineNote}</p>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2 sm:items-end">
            {authFactory ? (
              <div className="flex flex-wrap items-center justify-end gap-2">
                {ownerAccepted ? (
                  <span className="factory-accept-stamp factory-accept-stamp--static" aria-label="已验收">
                    已验收
                  </span>
                ) : (
                  <label
                    className="flex max-w-[11.5rem] cursor-pointer items-start gap-2 text-xs leading-snug text-ink sm:max-w-[13rem]"
                    title="启动/重启前设置。关=单页直通（页图→提取，跳过文档块切分）；开=文档块切分后跨页接缝"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 shrink-0"
                      checked={enableSemanticSplit}
                      disabled={busy || pipelineLive}
                      onChange={(e) => void persistSemanticSplitOption(e.target.checked)}
                    />
                    <span>
                      <span className="font-medium">跨页语义切分</span>
                      <span className="mt-0.5 block text-[11px] text-muted">
                        {enableSemanticSplit ? '跨页文档块路径' : '关=单页直通'}
                      </span>
                    </span>
                  </label>
                )}
                <div className="inline-flex flex-wrap items-center justify-end gap-1.5" role="group" aria-label="提取与工序操作">
                  {!ownerAccepted && batch?.pipelinePaused ? (
                    <button
                      type="button"
                      disabled={busy}
                      aria-label="恢复工序"
                      onClick={() => void onResumePipeline()}
                      className="group relative inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-white disabled:opacity-50"
                    >
                      {busy ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
                      <span
                        role="tooltip"
                        className="pointer-events-none absolute top-[calc(100%+8px)] right-0 z-50 w-max max-w-[14rem] rounded-md border border-[#3a4450] bg-[#1a2028] px-2.5 py-1.5 text-left text-[11px] font-medium leading-snug text-[#E8ECF1] opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                      >
                        恢复：在途调用结束后继续领取新的并发调用（只影响本任务）
                      </span>
                    </button>
                  ) : null}
                  {!ownerAccepted && pipelineLive && !batch?.pipelinePaused ? (
                    <button
                      type="button"
                      disabled={busy}
                      aria-label="暂停工序"
                      onClick={() => void onPausePipeline()}
                      className="group relative inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-ink hover:border-primary hover:text-primary disabled:opacity-50"
                    >
                      {busy ? <Loader2 size={16} className="animate-spin" /> : <Pause size={16} />}
                      <span
                        role="tooltip"
                        className="pointer-events-none absolute top-[calc(100%+8px)] right-0 z-50 w-max max-w-[14rem] rounded-md border border-[#3a4450] bg-[#1a2028] px-2.5 py-1.5 text-left text-[11px] font-medium leading-snug text-[#E8ECF1] opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                      >
                        暂停：等当前调用结束后不再开新并发，不急停、不影响其它任务
                      </span>
                    </button>
                  ) : null}
                  {!ownerAccepted && canContinuePipeline ? (
                    <button
                      type="button"
                      disabled={busy}
                      aria-label="从检查点继续"
                      onClick={() => setActionConfirm('continue_pipeline')}
                      className={
                        layoutFailed || anyPhaseFailed || pipelineStalled
                          ? 'group relative inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-white disabled:opacity-50'
                          : 'group relative inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-ink hover:border-primary hover:text-primary disabled:opacity-50'
                      }
                    >
                      {busy && actionConfirm === null
                        ? <Loader2 size={16} className="animate-spin" />
                        : <Play size={16} />}
                      <span
                        role="tooltip"
                        className="pointer-events-none absolute top-[calc(100%+8px)] right-0 z-50 w-max max-w-[14rem] rounded-md border border-[#3a4450] bg-[#1a2028] px-2.5 py-1.5 text-left text-[11px] font-medium leading-snug text-[#E8ECF1] opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                      >
                        从检查点继续：中断卡住 Job，从「{continueFromName || '下一未完成工序'}」续跑（已落盘页图/成功页会跳过）
                      </span>
                    </button>
                  ) : null}
                  {!ownerAccepted && canContinueExtract ? (
                    <>
                      {!canContinuePipeline ? (
                        <button
                          type="button"
                          disabled={busy}
                          aria-label="继续提取"
                          onClick={() => setActionConfirm('continue')}
                          className="group relative inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-ink hover:border-primary hover:text-primary disabled:opacity-50"
                        >
                          <Play size={16} />
                          <span
                            role="tooltip"
                            className="pointer-events-none absolute top-[calc(100%+8px)] right-0 z-50 w-max max-w-[14rem] rounded-md border border-[#3a4450] bg-[#1a2028] px-2.5 py-1.5 text-left text-[11px] font-medium leading-snug text-[#E8ECF1] opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                          >
                            继续提取：只重跑未成功的页/单元，已成功颗粒保留
                          </span>
                        </button>
                      ) : null}
                      <button
                        type="button"
                        disabled={busy}
                        aria-label="重抽全部页"
                        onClick={() => setActionConfirm('reextract_all')}
                        className="group relative inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-ink hover:border-primary hover:text-primary disabled:opacity-50"
                      >
                        <RefreshCw size={16} />
                        <span
                          role="tooltip"
                          className="pointer-events-none absolute top-[calc(100%+8px)] right-0 z-50 w-max max-w-[14rem] rounded-md border border-[#3a4450] bg-[#1a2028] px-2.5 py-1.5 text-left text-[11px] font-medium leading-snug text-[#E8ECF1] opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                        >
                          重抽全部页：按当前学科策略重抽（保留 PDF/页单元，只清旧颗粒）
                        </span>
                      </button>
                    </>
                  ) : null}
                  <div className="compare-phase-toggle" role="tablist" aria-label="工序">
                    {visibleList.map((p) => {
                      const label =
                        p.phase === 'layout' && !enableSemanticSplit
                          ? '页图准备'
                          : (phaseLabel[p.phase] || p.phase);
                      const active = (headerPhase || currentPhase) === p.phase;
                      const canOpen =
                        (isTerminalOk(p.status)
                          || ((p.phase === 'extract' || p.phase === 'ttq' || p.phase === 'ipttq')
                            && (isLiveStatus(p.status) || p.status === 'failed' || isTerminalOk(p.status))))
                        && p.status !== 'skipped_optional'
                        && p.status !== 'pending';
                      return (
                        <button
                          key={p.phase}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          className={active ? 'active' : undefined}
                          title={
                            canOpen
                              ? `查看「${label}」结果`
                              : `工序「${label}」· ${statusLabelForPhase(p, list.indexOf(p), list)}`
                          }
                          onClick={() => {
                            setHeaderPhase(p.phase);
                            if (canOpen) {
                              navigate(`/factory/tasks/${batchId}/results?phase=${p.phase}`);
                              return;
                            }
                            const el = document.getElementById(`factory-phase-${p.phase}`);
                            el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                          }}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  {!ownerAccepted ? (
                    <button
                      type="button"
                      disabled={busy || (pipelineLive && !pipelineStalled)}
                      aria-label="重新启动全部工序"
                      onClick={() => {
                        if (pipelineLive && !pipelineStalled) return;
                        setMoreMenuOpen(false);
                        setActionConfirm('restart');
                      }}
                      className="group relative inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-ink hover:border-primary hover:text-primary disabled:opacity-50"
                    >
                      <RotateCcw size={16} />
                      <span
                        role="tooltip"
                        className="pointer-events-none absolute top-[calc(100%+8px)] right-0 z-50 w-max max-w-[14rem] rounded-md border border-[#3a4450] bg-[#1a2028] px-2.5 py-1.5 text-left text-[11px] font-medium leading-snug text-[#E8ECF1] opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                      >
                        {pipelineLive && !pipelineStalled
                          ? '工序进行中，请稍候或待完成后重试'
                          : '重新启动全部工序：删除原始 PDF 之外的全部衍生数据，并从首道工序重跑'}
                      </span>
                    </button>
                  ) : null}
                  <div className="relative" ref={moreMenuRef}>
                    <button
                      type="button"
                      aria-label="更多操作"
                      aria-expanded={moreMenuOpen}
                      disabled={busy}
                      onClick={() => setMoreMenuOpen((v) => !v)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-ink hover:border-primary hover:text-primary disabled:opacity-50"
                    >
                      <MoreVertical size={16} />
                    </button>
                    {moreMenuOpen ? (
                      <div
                        role="menu"
                        className="absolute right-0 top-[calc(100%+6px)] z-40 min-w-[9.5rem] overflow-hidden rounded-xl border border-border bg-surface py-1 shadow-lg"
                      >
                        <button
                          type="button"
                          role="menuitem"
                          disabled={busy || detailBusy}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-canvas disabled:opacity-50"
                          onClick={openTaskDetailPanel}
                        >
                          <Info size={14} />
                          任务详情
                        </button>
                        {!ownerAccepted ? (
                          <button
                            type="button"
                            role="menuitem"
                            disabled={busy}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-danger hover:bg-canvas disabled:opacity-50"
                            onClick={() => {
                              setMoreMenuOpen(false);
                              setActionConfirm('delete');
                            }}
                          >
                            <Trash2 size={14} />
                            删除任务
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
            {layoutAction ? (
              <button
                type="button"
                disabled={layoutAction.disabled}
                onClick={layoutAction.onClick || undefined}
                className="rounded-lg bg-primary px-3 py-1.5 text-sm text-white disabled:opacity-50"
              >
                {layoutAction.label}
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {error ? <p className="text-sm text-danger">{error}</p> : null}
      {message ? (
        <p
          className={
            artifactAccess
              ? 'rounded-xl border border-primary/25 bg-primary/5 px-4 py-3 text-sm text-ink'
              : 'rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900'
          }
        >
          {message}
        </p>
      ) : null}

      <section className="rounded-2xl border border-border bg-surface p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-ink">工序（Phase）</h2>
        <p className="mt-1 text-xs text-muted">
          {enableSemanticSplit
            ? '跨页：文档块切分 → 接缝 → 结构聚合 → 提取。目前仅数学开放颗粒提取与品题。'
            : '单页直通：每页整图 → 数学走 math-k12-extra / 其它走 extras_v2；不做文档块切分 / 结构聚合。目前仅数学开放颗粒提取与品题。'}
        </p>
        <p className="mt-1 text-xs text-muted">
          本任务累计 Token{' '}
          <strong className="font-semibold tabular-nums text-ink">
            {formatAvgTokens(
              detailUsage?.totalTokens
                ?? phaseMetrics.reduce((sum, m) => sum + (Number(m.totalTokens) || 0), 0),
              true,
            )}
          </strong>
          {detailUsage && (detailUsage.promptTokens > 0 || detailUsage.completionTokens > 0) ? (
            <span className="text-muted">
              {' '}
              （prompt {formatAvgTokens(detailUsage.promptTokens, true)}
              {' · '}
              completion {formatAvgTokens(detailUsage.completionTokens, true)}）
            </span>
          ) : null}
        </p>
        {authFactory
        && layoutDone
        && !layoutLive
        && (
          batch?.disciplineStatus === 'insufficient_sample'
          || batch?.disciplineStatus === 'ambiguous'
          || batch?.primaryDiscipline === 'unknown'
          || batch?.primaryDiscipline == null
        ) ? (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
            <span className="min-w-0 flex-1">
              {batch?.disciplineResampleHint
                || batch?.disciplineNote
                || (enableSemanticSplit
                  ? '学科未就绪：将从全文文档块随机采样后判定（规范名对齐 Mobile2：math / yuwen / english）。'
                  : '学科未就绪：将随机采页图调用 select-modality（最多 3 次）；仍不明时可再判定。')}
            </span>
            <button
              type="button"
              disabled={busy || pipelineLive}
              onClick={() => void onRejudgeDiscipline()}
              className="shrink-0 rounded-lg border border-amber-400/50 px-2.5 py-1 text-amber-50 hover:border-amber-300 disabled:opacity-50"
            >
              重新判定学科
            </button>
          </div>
        ) : null}

        {/* FrameworkAI-style pipeline header: timer + overall % + track */}
        <div className="mt-3 flex items-center justify-between gap-3 text-xs text-muted">
          <div className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5">
            {inFlight && !batch?.pipelinePaused ? (
              <Loader2 size={14} className="animate-spin text-primary" />
            ) : null}
            <span>
              已用时{' '}
              <strong className="font-semibold tabular-nums text-ink">{formatElapsed(totalElapsed)}</strong>
              {batch?.pipelinePaused ? (
                <span className="text-muted/90" title="暂停后墙钟冻结，不再累计">
                  {' '}· 已暂停
                  {pipelineLive ? '（在途收尾不计时）' : ''}
                </span>
              ) : inFlight ? (
                <>
                  {' '}
                  · 本步{' '}
                  <strong className="font-semibold tabular-nums text-ink">{formatElapsed(stageElapsed)}</strong>
                </>
              ) : null}
            </span>
            {formatDataRefreshLabel(dataRefreshedAt) ? (
              <span className="tabular-nums text-[11px] text-muted/80" title="台面轮询最近成功拉取时间">
                · {formatDataRefreshLabel(dataRefreshedAt)}
              </span>
            ) : null}
          </div>
          <span className="tabular-nums">{progressPct}%</span>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-canvas" aria-hidden>
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        {phaseMetrics.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] leading-snug text-muted">
            {phaseMetrics.map((m) => {
              const name = m.key === 'layout' && !enableSemanticSplit
                ? '页图准备'
                : (m.label || phaseLabel[m.key] || m.key);
              const countBit = formatPhaseUnitCount(m, resolvePhasePressure(batch, m, m.key));
              if (!m.started) {
                return (
                  <span key={m.key}>
                    {name}{' '}
                    <span className="text-muted/80">未开始</span>
                  </span>
                );
              }
              return (
                <span key={m.key} className="tabular-nums">
                  {name}{' '}
                  <span className="font-medium text-ink">
                    {countBit ? `${countBit} · ` : ''}
                    {formatAvgLatencyMs(m.avgLatencyMs, true)}
                    {' · '}
                    {formatAvgTokens(m.avgTokens, true)} Token
                    {Number(m.totalTokens) > 0
                      ? ` · 累计 ${formatAvgTokens(m.totalTokens, true)}`
                      : ''}
                  </span>
                </span>
              );
            })}
          </div>
        ) : null}

        <ol className="mt-3 space-y-2">
          {visibleList.map((p, index) => {
            const isCurrent = p.phase === currentPhase;
            const progress = resolvePhaseProgress(p, jobs, workerQueue);
            const expand = isCurrent;
            const statusLabel = statusLabelForPhase(p, list.indexOf(p), list);
            const metric = metricForPhase(phaseMetrics, p.phase);
            const pressure = resolvePhasePressure(batch, metric, p.phase);
            const jobLive = isLiveStatus(p.status) || isLiveStatus(progress.job?.status || '');
            const pressureAlert = Boolean(pressure?.needsManual);
            const pressureIdle = Boolean(pressure?.short && !pressure?.needsManual && !jobLive);
            const done = isTerminalOk(p.status) && !pressureAlert && !pressureIdle && !pressure?.short;
            const failed = p.status === 'failed' || pressureAlert;
            const live = jobLive;
            const metricCaption = phaseMetricCaptions[p.phase];
            const refreshLabel = formatDataRefreshLabel(dataRefreshedAt);
            const jobUpdatedLabel = formatJobUpdatedLabel(progress.job?.updatedAt, now);

            // 提取/浓缩进行中也可点进结果页，看已产出内容。
            const canOpenResults =
              (done
                || pressureIdle
                || ((p.phase === 'extract' || p.phase === 'ttq' || p.phase === 'ipttq' || p.phase === 'figure_extract')
                  && (live || failed || done || isTerminalOk(p.status))))
              && p.status !== 'skipped_optional'
              && p.status !== 'pending'
              && (p.phase === 'semantic_split'
                || p.phase === 'layout'
                || p.phase === 'structure'
                || p.phase === 'extract'
                || p.phase === 'figure_extract'
                || p.phase === 'ttq'
                || p.phase === 'ipttq');

            return (
              <li
                key={p.phase}
                id={`factory-phase-${p.phase}`}
                role={canOpenResults ? 'button' : undefined}
                tabIndex={canOpenResults ? 0 : undefined}
                onClick={() => {
                  if (!canOpenResults) return;
                  navigate(`/factory/tasks/${batchId}/results?phase=${p.phase}`);
                }}
                onKeyDown={(e) => {
                  if (!canOpenResults) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/factory/tasks/${batchId}/results?phase=${p.phase}`);
                  }
                }}
                className={`relative rounded-xl border px-3 py-2 text-sm ${
                  failed
                    ? 'border-danger/40 bg-danger/5'
                    : pressureIdle
                      ? 'border-amber-500/40 bg-amber-500/[0.06]'
                      : isCurrent
                        ? 'border-primary bg-primary/5'
                        : done
                          ? 'border-primary/30 bg-primary/[0.04]'
                          : 'border-border/80'
                } ${canOpenResults ? 'cursor-pointer transition hover:border-primary/60' : ''}`}
              >
                <div className="flex gap-2.5">
                  <div
                    className={`grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full text-[11px] font-bold ${
                      failed
                        ? 'bg-danger text-white'
                        : live
                          ? 'bg-primary text-white'
                          : pressureIdle
                            ? 'bg-amber-500 text-white'
                            : isCurrent || done
                              ? 'bg-primary text-white'
                              : 'bg-canvas text-muted'
                    }`}
                  >
                    {live ? (
                      <Loader2 size={12} className="animate-spin text-white" />
                    ) : pressureAlert ? (
                      <TriangleAlert size={12} className="text-white" />
                    ) : pressureIdle ? (
                      <Pause size={12} className="text-white" />
                    ) : (
                      index + 1
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="font-medium text-ink">
                        {p.phase === 'layout' && !enableSemanticSplit
                          ? '页图准备'
                          : (phaseLabel[p.phase] || p.phase)}
                        {isCurrent ? (
                          <span className="ml-2 text-xs font-normal text-primary">当前</span>
                        ) : null}
                        {canOpenResults ? (
                          <span className="ml-2 text-xs font-normal text-muted">查看结果 →</span>
                        ) : null}
                      </div>
                      {/* Status + big live pct so progress is obvious at a glance */}
                      <span className="text-xs tabular-nums text-muted">
                        {statusLabel}
                        {live ? (
                          <span className="ml-1.5 inline-flex items-center gap-1 font-semibold text-primary">
                            <span className="text-sm">{progress.pct}%</span>
                          </span>
                        ) : null}
                      </span>
                    </div>
                    {expand && live ? (
                      <p className="mt-1 flex items-center gap-1.5 text-xs font-medium text-primary">
                        <Loader2 size={12} className="shrink-0 animate-spin" />
                        <span className="tabular-nums">{progress.label}</span>
                      </p>
                    ) : null}
                    <p className="mt-0.5 text-xs text-muted">
                      {hintForPhase(p.phase)}
                    </p>
                    {metricCaption ? (
                      <p className="mt-1 text-[11px] tabular-nums text-muted">
                        {!metric || !metric.started ? (
                          <>
                            <span>
                              {p.phase === 'ipttq' && (live || p.status === 'pending')
                                ? (live ? '进行中' : '等待品题推荐完成后开始')
                                : '未开始'}
                            </span>
                            {refreshLabel && (live || isCurrent) ? (
                              <>
                                <span className="mx-1.5 text-[#6b7583]">·</span>
                                <span className="text-muted/90" title="台面轮询最近成功拉取时间">
                                  {refreshLabel}
                                </span>
                              </>
                            ) : null}
                            {jobUpdatedLabel && live ? (
                              <>
                                <span className="mx-1.5 text-[#6b7583]">·</span>
                                <span
                                  className={
                                    /无推进/.test(jobUpdatedLabel)
                                      ? 'font-medium text-amber-200/90'
                                      : 'text-muted/90'
                                  }
                                  title="服务端 Job 最近 progress 更新时间"
                                >
                                  {jobUpdatedLabel}
                                </span>
                              </>
                            ) : null}
                          </>
                        ) : (
                          <>
                            {formatPhaseUnitCount(metric, pressure) ? (
                              <>
                                <span className="font-semibold text-ink">
                                  {formatPhaseUnitCount(metric, pressure)}
                                </span>
                                <span className="mx-1.5 text-[#6b7583]">·</span>
                              </>
                            ) : null}
                            <span>
                              {metricCaption.latency}{' '}
                              <span className="font-semibold text-ink">
                                {formatAvgLatencyMs(metric.avgLatencyMs, true)}
                              </span>
                            </span>
                            <span className="mx-1.5 text-[#6b7583]">·</span>
                            <span>
                              {metricCaption.tokens}{' '}
                              <span className="font-semibold text-ink">
                                {formatAvgTokens(metric.avgTokens, true)}
                              </span>
                            </span>
                            <span className="mx-1.5 text-[#6b7583]">·</span>
                            <span>
                              累计 Token{' '}
                              <span className="font-semibold text-ink">
                                {formatAvgTokens(metric.totalTokens, true)}
                              </span>
                            </span>
                            {refreshLabel ? (
                              <>
                                <span className="mx-1.5 text-[#6b7583]">·</span>
                                <span className="text-muted/90" title="台面轮询最近成功拉取时间">
                                  {refreshLabel}
                                </span>
                              </>
                            ) : null}
                            {jobUpdatedLabel && live ? (
                              <>
                                <span className="mx-1.5 text-[#6b7583]">·</span>
                                <span
                                  className={
                                    /无推进/.test(jobUpdatedLabel)
                                      ? 'font-medium text-amber-200/90'
                                      : 'text-muted/90'
                                  }
                                  title="服务端 Job 最近 progress 更新时间"
                                >
                                  {jobUpdatedLabel}
                                </span>
                              </>
                            ) : null}
                            {live
                              && p.phase === 'ipttq'
                              && (Number(metric.totalTokens) || 0) === 0
                              && /bank_stock|product_stock|诱导|ipttq_condense/.test(
                                String(progress.label || ''),
                              ) ? (
                              <span className="mt-0.5 block text-[10px] text-muted">
                                本步在生成诱导题库（FC），Token 可能暂不计入本工序
                              </span>
                            ) : null}
                          </>
                        )}
                      </p>
                    ) : refreshLabel && (live || isCurrent || failed) ? (
                      <p className="mt-1 text-[11px] tabular-nums text-muted/90" title="台面轮询最近成功拉取时间">
                        {refreshLabel}
                      </p>
                    ) : null}

                    {p.phase === 'extract' && extractFailedCount > 0 ? (
                      <div className="mt-2 flex justify-end">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 rounded-md border border-amber-500/45 bg-amber-500/10 px-2 py-1 text-[11px] font-semibold text-amber-300 hover:bg-amber-500/20"
                          title={`有 ${extractFailedCount} 页提取失败，点击查看并处理`}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/factory/tasks/${batchId}/results?phase=extract&warn=extract`,
                            );
                          }}
                        >
                          <TriangleAlert size={13} strokeWidth={2.25} aria-hidden />
                          失败 {extractFailedCount}
                        </button>
                      </div>
                    ) : null}

                    {/* Live progress expands only while this step is queued/running/failed */}
                    {expand && (live || failed) ? (
                      <div className="mt-2.5 space-y-2">
                        {failed ? (
                          <div className="flex items-start gap-2 text-xs leading-snug text-danger">
                            <span className="tabular-nums">
                              {isWorkerRequeueMarker(progress.job?.errorMessage)
                                ? '工序失败'
                                : (progress.job?.errorMessage || '工序失败')}
                            </span>
                          </div>
                        ) : null}
                        {live && liveFcFeedbackMessage(progress.job) ? (
                          <p className="text-[11px] leading-snug text-amber-200/90">
                            最近 FC 反馈：{liveFcFeedbackMessage(progress.job)}
                          </p>
                        ) : null}
                        <div className="flex items-center gap-2">
                          <div
                            className="h-2.5 min-w-0 flex-1 overflow-hidden rounded-full bg-canvas"
                            role="progressbar"
                            aria-valuenow={progress.pct}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-label={progress.label || statusLabel}
                          >
                            <div
                              className={`h-full rounded-full transition-[width] duration-300 ease-out ${
                                failed ? 'bg-danger' : 'bg-primary'
                              }`}
                              style={{
                                width: `${live ? Math.max(progress.pct, 6) : progress.pct}%`,
                              }}
                            />
                          </div>
                          {live ? (
                            <span className="shrink-0 text-xs font-semibold tabular-nums text-primary">
                              {progress.pct}%
                            </span>
                          ) : null}
                        </div>
                        {live ? (
                          <p className="text-[10px] tabular-nums text-muted/80">
                            <span title="台面轮询最近成功拉取时间">{refreshLabel}</span>
                            {jobUpdatedLabel ? (
                              <>
                                <span className="mx-1.5">·</span>
                                <span
                                  className={/无推进/.test(jobUpdatedLabel) ? 'text-amber-200/90' : undefined}
                                  title="服务端 Job 最近 progress 更新时间"
                                >
                                  {jobUpdatedLabel}
                                </span>
                              </>
                            ) : null}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </section>

      {actionConfirm ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4"
          role="presentation"
          onClick={() => !busy && setActionConfirm(null)}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="factory-action-confirm-title"
            aria-describedby="factory-action-confirm-desc"
            className="w-full max-w-md rounded-2xl border border-border bg-surface p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="factory-action-confirm-title" className="text-base font-semibold text-ink">
              {actionConfirm === 'continue_pipeline'
                ? '确认从检查点继续？'
                : actionConfirm === 'continue'
                  ? '确认继续提取？'
                  : actionConfirm === 'reextract_all'
                    ? '确认重抽全部页？'
                    : actionConfirm === 'delete'
                      ? '确认删除任务？'
                      : '确认重新启动全部工序？'}
            </h3>
            <p id="factory-action-confirm-desc" className="mt-3 text-sm leading-relaxed text-muted">
              {actionConfirm === 'continue_pipeline' ? (
                <>
                  将中断卡住的 Job，并从「<strong className="font-medium text-ink">{continueFromName || '当前工序'}</strong>」
                  续跑。不清原始 PDF；已落盘页图、已成功提取页、已完整品题组会跳过。
                </>
              ) : actionConfirm === 'continue' ? (
                <>
                  将只重跑<strong className="font-medium text-ink">尚未成功</strong>
                  的页/单元；已提取成功的知识颗粒会保留，不会清空。
                </>
              ) : actionConfirm === 'reextract_all' ? (
                <>
                  将按当前学科策略重抽<strong className="font-medium text-ink">全部页</strong>
                  ：保留 PDF / 页单元，只清除旧颗粒结果并覆盖。已成功的提取也会重跑。
                </>
              ) : actionConfirm === 'delete' ? (
                <>
                  将删除本任务及其加工产物（页图、提取、品题入围等）。货架上的原始 PDF 一般会保留；此操作不可撤销。
                </>
              ) : (
                <>
                  将删除<strong className="font-medium text-ink">原始 PDF 之外</strong>
                  的全部衍生数据：版面分册、文档块切分结果、语义切分接缝、结构聚合前体、颗粒提取产物及对应 OSS；
                  并取消进行中的 Job，从首道工序重新排队
                  {enableSemanticSplit ? '（跨页：文档块切分路径）' : '（单页直通：页图→提取）'}。
                  计时将归零。
                </>
              )}
            </p>
            {actionConfirm === 'restart' ? (
              <p className="mt-2 text-sm text-muted">
                原始 PDF / 货架源文件会保留。若只要补跑失败/卡住工序，请取消并用「从检查点继续」。
              </p>
            ) : null}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                disabled={busy}
                className="rounded-lg border border-border px-3 py-1.5 text-sm hover:border-primary disabled:opacity-50"
                onClick={() => setActionConfirm(null)}
              >
                取消
              </button>
              <button
                type="button"
                disabled={busy}
                className={
                  actionConfirm === 'delete'
                    ? 'rounded-lg px-3 py-1.5 text-sm text-white disabled:opacity-50'
                    : 'rounded-lg bg-primary px-3 py-1.5 text-sm text-white disabled:opacity-50'
                }
                style={actionConfirm === 'delete' ? { backgroundColor: '#c45c63' } : undefined}
                onClick={() => void confirmAction()}
              >
                {busy
                  ? '处理中…'
                  : actionConfirm === 'continue_pipeline'
                    ? '确认从检查点继续'
                    : actionConfirm === 'continue'
                      ? '确认继续提取'
                      : actionConfirm === 'reextract_all'
                        ? '确认重抽全部页'
                        : actionConfirm === 'delete'
                          ? '确认删除'
                          : '确认重启并清理衍生物'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {detailOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="presentation"
          onClick={() => setDetailOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="factory-task-detail-title"
            className="w-full max-w-md rounded-2xl border border-border bg-surface p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="factory-task-detail-title" className="text-base font-semibold text-ink">
              任务详情
            </h3>
            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-muted">标题</dt>
                <dd className="text-right text-ink">{batch?.title || '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted">状态</dt>
                <dd className="text-right text-ink">
                  {batch ? (factoryStatusLabel[batch.status] || batch.status) : '—'}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted">学科</dt>
                <dd className="text-right text-ink">
                  {disciplineLabel[String(batch?.primaryDiscipline || batch?.mobile2Subject || 'unknown')]
                    || '—'}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted">跨页切分</dt>
                <dd className="text-right text-ink">{enableSemanticSplit ? '开' : '关'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted">任务 ID</dt>
                <dd className="flex max-w-[70%] items-start justify-end gap-1.5">
                  <span className="break-all text-right text-xs text-muted">{batchId}</span>
                  <button
                    type="button"
                    className="shrink-0 rounded-md border border-border p-1 text-muted hover:bg-canvas hover:text-ink"
                    title={idCopied ? '已复制' : '复制任务 ID'}
                    aria-label={idCopied ? '已复制' : '复制任务 ID'}
                    onClick={() => {
                      void navigator.clipboard.writeText(batchId).then(() => {
                        setIdCopied(true);
                        window.setTimeout(() => setIdCopied(false), 1500);
                      }).catch(() => undefined);
                    }}
                  >
                    {idCopied ? <Check size={14} className="text-primary" /> : <Copy size={14} />}
                  </button>
                </dd>
              </div>
            </dl>

            <div className="mt-4 border-t border-border pt-4">
              <h4 className="text-sm font-semibold text-ink">Token 消耗</h4>
              {detailBusy ? (
                <p className="mt-2 flex items-center gap-2 text-sm text-muted">
                  <Loader2 size={14} className="animate-spin" />
                  汇总中…
                </p>
              ) : null}
              {detailError ? <p className="mt-2 text-sm text-danger">{detailError}</p> : null}
              {!detailBusy && detailUsage ? (
                <div className="mt-2 space-y-2 text-sm">
                  <div className="flex justify-between gap-3">
                    <span className="text-muted">合计</span>
                    <span className="tabular-nums font-medium text-ink">
                      {detailUsage.totalTokens.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3 text-xs">
                    <span className="text-muted">prompt / completion</span>
                    <span className="tabular-nums text-muted">
                      {detailUsage.promptTokens.toLocaleString()}
                      {' / '}
                      {detailUsage.completionTokens.toLocaleString()}
                    </span>
                  </div>
                  {Object.keys(detailUsage.byPhase).length > 0 ? (
                    <ul className="mt-2 space-y-1 border-t border-border/60 pt-2 text-xs text-muted">
                      {Object.entries(detailUsage.byPhase)
                        .filter(([, b]) => b.totalTokens > 0)
                        .map(([phase, b]) => (
                          <li key={phase} className="flex justify-between gap-2">
                            <span>{phaseLabel[phase] || phase}</span>
                            <span className="tabular-nums">{b.totalTokens.toLocaleString()}</span>
                          </li>
                        ))}
                    </ul>
                  ) : null}
                  {detailUsage.notes.includes('no_stored_usage_yet') ? (
                    <p className="mt-2 text-xs text-muted">
                      尚无落库的 token 明细（部分工序未回写 usage）。合计可能偏低。
                    </p>
                  ) : null}
                  {detailUsage.phaseMetrics && detailUsage.phaseMetrics.length > 0 ? (
                    <ul className="mt-3 space-y-1 border-t border-border/60 pt-2 text-xs text-muted">
                      {detailUsage.phaseMetrics.map((m) => {
                        const cap = phaseMetricCaptions[m.key];
                        return (
                          <li key={m.key} className="flex justify-between gap-2">
                            <span>{m.label}</span>
                            <span className="tabular-nums font-medium text-ink">
                              {!m.started
                                ? '未开始'
                                : `${formatPhaseUnitCount(m, resolvePhasePressure(batch, m, m.key)) ? `${formatPhaseUnitCount(m, resolvePhasePressure(batch, m, m.key))} · ` : ''}${formatAvgLatencyMs(m.avgLatencyMs, true)} · ${formatAvgTokens(m.avgTokens, true)}${cap ? ` /${m.unitLabel}` : ''}`}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                className="rounded-lg border border-border px-3 py-1.5 text-sm hover:border-primary"
                onClick={() => setDetailOpen(false)}
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
